import { Suspense, useEffect, useState } from "react";

/** Nextjs */
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";

/** MUI */
import { Box, Button, Container, Divider, Grid, Paper, Typography, useMediaQuery } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

/** - icons */
import { ExpandLess, ExpandMore } from "@mui/icons-material";

/** Redux */
import { getDashboardTOC, setSkipLinksPage } from "@/src/_appStore/Common";
import { useDispatch, useSelector } from "react-redux";
import { setAreaForPanel } from "../../../_appStore/DB_Common";
import { setArea } from "../../../_appStore/VAL_Common";

/** Helpers */
import clsx from "clsx";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import useHelper from "../../../_common/useHelper";
/** matomo */
import { useMatomo } from "@datapunt/matomo-tracker-react";

/** API */
import { postModulesForAreaByIDFromAPI } from "../../../_APIs";

/** UI */

/** UI */
import Preloader from "../../../_common/Preloader";
import TopChartCard from "../../HomePage/AreasPresentationTabbed/components/WidgetCard";
import ChartCard from "../_components/ChartCard";
import HeaderInfoWidget from "../_components/HeaderInfoWidget";
import TableOfContents from "../_components/TOC/TableOfContents";
import PageTitle from "../_components/PageTitle";
import DashboardSkeleton from "../skeletons/DashboardSkeleton";
import SronlyH3 from "@/src/components/srOnly/SronlyH3";

const AppTooltip = dynamic(() => import("../../../_common/AppTooltip"));

const MIN_INTRO_HEIGHT = 600;
const MAX_CHART_HEIGHT = 680;

const useStyles = makeStyles((theme) => ({
    topContainer: {
        height: "calc(100vh - 100px)",
    },
    rootContainer: {
        [theme.breakpoints.down("md")]: {
            padding: theme.spacing(0, 2),
        },
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(0, 1),
        },
    },
    BannerContainer: {
        height: "calc(31.25vw)",
        backgroundColor: "#105e5e",
        backgroundPosition: "center center",
        backgroundSize: "cover",
        paddingTop: 1,
        "& > div": {
            width: "100%",
        },
        [theme.breakpoints.down("md")]: {
            height: "auto",
            paddingBottom: theme.spacing(2),
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
        },
        ["@media (max-width: 280px)"]: {
            padding: theme.spacing(0, 1),
        },
    },
    PageTitle: {
        fontSize: "calc(1.6666667vw)",
        height: "calc(3.33vw)",
        fontWeight: 700,
        display: "flex",
        minWidth: 523,
        alignItems: "center",
        marginTop: 0,
        [theme.breakpoints.down("xl")]: {
            fontSize: "1.33333334rem",
            flexGrow: 1,
        },
        [theme.breakpoints.down("lg")]: {},
        [theme.breakpoints.down("md")]: {
            minWidth: "calc(100vw - 64px)",
            height: "auto",
            justifyContent: "space-between",
            fontSize: "1.3333333rem",
        },
        [theme.breakpoints.down("sm")]: {},
        ["@media (max-width: 280px)"]: {
            height: "auto",
            fontSize: "1.77777778rem",
            marginTop: theme.spacing(2),
        },
    },
    PageTitleName: {
        [theme.breakpoints.down("md")]: {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap!important",
        },
    },
    AreaTitle: {
        fontSize: "calc(2.5vw)",
        fontWeight: 500,
        color: "#FFF",
        marginTop: "calc(3.125vw)",
        [theme.breakpoints.down("md")]: {
            fontSize: "2.66666667rem",
            textAlign: "left",
            padding: theme.spacing(1),
            marginTop: theme.spacing(2),
        },
        ["@media (max-width: 280px)"]: {
            fontSize: "1.77777778rem",
        },
    },
    MenuHolder: {
        ...theme.shape.boxedWrapper,
        margin: theme.spacing(4, 2),
        minHeight: "calc(100% - 64px)",
    },
    cardsHolder: {
        marginTop: 20,
    },
    ContentHolder: {
        ...theme.shape.boxedWrapper,
        borderColor: "transparent",
        boxShadow: "none",
        padding: theme.spacing(0, 3, 13, 3),
        borderRadius: 0,
        "&:focus-visible": {
            ...theme.outliness,
        },
        [theme.breakpoints.down("md")]: {
            padding: theme.spacing(0, 1),
        },
        ["@media (max-width: 280px)"]: {
            padding: theme.spacing(0, 1),
        },
    },
    menuLeftAccent: {
        borderLeft: `2px solid ${theme.palette.secondary.light}`,
        paddingLeft: theme.spacing(2),
    },
    menuList: {},
    moduleDiv: {
        width: "100%",
        display: "flex",
        alignItems: "center",
    },
    modTitle: {
        fontSize: "1.33rem",
        "&:focus-visible": {
            ...theme.outliness,
        },
        [theme.breakpoints.down("xl")]: {
            fontSize: "1.11111111rem",
        },
        [theme.breakpoints.down("lg")]: {
            fontSize: "1rem",
        },
    },
    boxedWrapper: {
        ...theme.shape.boxedWrapper,
        [theme.breakpoints.down("lg")]: {
            padding: theme.spacing(1, 1),
        },
    },
    chartPaper: {
        maxHeight: MAX_CHART_HEIGHT,
        "@media (max-width: 1199.95px)": {
            maxHeight: "unset",
        },
    },
    introPaper: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: MAX_CHART_HEIGHT - 40,
        [theme.breakpoints.down("md")]: {
            minHeight: 200,
        },
        "& > div": {
            minHeight: MIN_INTRO_HEIGHT - 25,
            [theme.breakpoints.down("md")]: {
                minHeight: 200,
            },
        },
    },
    introContentHidden: {
        fontSize: "1rem",
        [theme.breakpoints.down("lg")]: {
            fontSize: "0.88888889rem",
        },
        height: MIN_INTRO_HEIGHT - 25,
        [theme.breakpoints.down("md")]: {
            height: 200,
        },
        overflow: "hidden",
    },
    introContentVisible: {
        height: "100%",
        fontSize: "1rem",
        [theme.breakpoints.down("lg")]: {
            fontSize: "0.88888889rem",
        },
    },
    nameIcon: {
        fontSize: "1rem",
        width: 51,
        height: 51,
        color: "#FFF",
        backgroundColor: theme.palette.primary.main,
        borderRadius: "50%",
        boxShadow: theme.shadows[2],
        padding: theme.spacing(1.5),
    },
    expandBtn: {
        opacity: 1,
        position: "relative",
        boxShadow: "0px -12px 36px rgba(255,255,255,1)",
    },
    expandBtnDisabled: {
        opacity: 0,
        "& svg": {
            color: "#ccc",
        },
    },
    LinkBtn: {
        textTransform: "none",
        fontSize: "1.11111111rem",
        letterSpacing: "0.00938em",
        fontWeight: "normal",
        borderRadius: 0,
        padding: 0,
        "& + &": {
            paddingLeft: theme.spacing(1),
            [theme.breakpoints.up("md")]: {},
        },
        "&:hover": {
            textDecoration: "underline",
            backgroundColor: "transparent",
        },
        [theme.breakpoints.down("lg")]: {
            fontSize: "1rem",
            height: 40,
        },
        [theme.breakpoints.down("md")]: {
            padding: 0,
        },
        [theme.breakpoints.down("sm")]: {
            fontSize: "0.88888889rem",
        },
    },
    topLinksWrap: {
        display: "flex",
        gap: theme.spacing(3),
        marginRight: 8,
        [theme.breakpoints.down("lg")]: {
            gap: theme.spacing(1),
            marginRight: 4,
        },
        [theme.breakpoints.down("md")]: {
            gap: theme.spacing(1),
        },
        [theme.breakpoints.down("sm")]: {
            // flexGrow: 1,
            maxWidth: 280,
            justifyContent: "space-between",
        },
        ["@media (max-width: 320px)"]: {
            maxWidth: "unset",
            width: "100%",
        },
    },
}));

const IndexIndex = (props) => {
    let { areas, area, areaID } = props;
    // process.env.NODE_ENV === "development" && console.log("%c IndexIndex: ", "background: #999; color:#FFF", props);

    const { t, i18n } = useTranslation();
    const { translationFromJson } = useHelper();
    const classes = useStyles();
    const router = useRouter();
    const dispatch = useDispatch();
    const { trackPageView } = useMatomo();

    const downMd = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const downLg = useMediaQuery((theme) => theme.breakpoints.down("lg"));
    const downXl = useMediaQuery((theme) => theme.breakpoints.down("xl"));

    /** States */
    const [ERROR, setERROR] = useState(false);
    const [IS_CALC, setIS_CALC] = useState(false);
    const [LOADING, setLOADING] = useState(false);
    const [MODULES, setMODULES] = useState(null);
    const [OPENED_INTROS, setOPENED_INTROS] = useState([]);
    const [INTRO_HEIGHTS, setINTRO_HEIGHTS] = useState({});
    /** /end States */

    /** Effects */
    /** - API */
    useEffect(() => {
        if (MODULES) return;
        setLOADING(true);

        postModulesForAreaByIDFromAPI({ id_obszar: areaID || area.id, czy_pl: i18n.language === "pl" })
            .then((response) => {
                response =
                    response instanceof Array
                        ? response[0] instanceof Object
                            ? response[0]
                            : {}
                        : response instanceof Object
                        ? response
                        : {};

                if (response.message && response.message.type === "Error") {
                    setERROR(result);
                } else {
                    const _withUniqByChartId = {
                        ...response,
                        modules: response?.modules.map((module) => {
                            return {
                                ...module,
                                charts: _.uniqBy(module.charts, "id"),
                            };
                        }),
                    };

                    setIS_CALC(response.is_calc);

                    setMODULES(_withUniqByChartId);
                }
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                setLOADING(false);
                // setTimeout(() => {
                //     setLOADING(false);
                // }, 3000);
            });
    }, []);

    useEffect(() => {
        const moduleId = router.asPath.split("#!");
        if (moduleId[1]) {
            setTimeout(() => {
                showModule({ eid: moduleId[1] });
            }, 1000);
        }
    }, [router]);

    useEffect(() => {
        if (!MODULES?.modules?.length) return;

        handleIntroHeights();

        window.addEventListener("resize", handleIntroHeights);
        return () => window.removeEventListener("resize", handleIntroHeights);
    }, [MODULES]);

    // useEffect(() => trackPageView(), []);

    useEffect(() => {
        const prepend = `${t("Opis strony")} "${translationFromJson(area, "name")} - ${t("Dashboardy")}" ${t("page")} ${t(
            "dla czytnika ekranu"
        )}. `;

        const title = translationFromJson(area, "name") || t("Ładowanie");

        dispatch(setSkipLinksPage({ id: "dashboard", prepend, title }));
        return () => dispatch(setSkipLinksPage(""));
    }, [MODULES, area]);

    /** - focus skiplinks */
    useEffect(() => {
        setTimeout(() => {
            const SCROLLBAR = document.querySelector("#appScroll > .os-viewport");
            SCROLLBAR?.scrollTo({ top: 0, behavior: "smooth" });

            const _skipMenu = document.getElementById("__skip-links-holder__");
            if (_skipMenu) {
                _skipMenu?.focus();
            }
        }, 100);
    }, [LOADING]);

    /** /end Effects */

    /** Handlers */
    const handleIntroHeights = () => {
        const _intro_heights = {};
        MODULES?.modules?.map((mod) => {
            const paper = document.querySelector(`#intro-for-${mod.id}`);
            const element = document.querySelector(`#intro-for-${mod.id} > div > div`);
            const _browserZoom = ((window.outerWidth - 0) / window.innerWidth) * 1;
            if (element && paper) {
                _intro_heights[`zoom`] = _browserZoom;
                _intro_heights[`paper`] = paper.getBoundingClientRect().height;
                _intro_heights[`${mod.id}`] = element.getBoundingClientRect().height; // * (1-_browserZoom+1);
            }
        });

        setINTRO_HEIGHTS(_intro_heights);
    };

    const showModule = ({ eid }) => {
        if (typeof window !== "undefined") {
            const yOffset = -140;
            const element = document.getElementById(eid);

            var scrollBarElement = document.querySelector("#appScroll .os-viewport");

            if (element && scrollBarElement) {
                const y = eid === "main" ? 0 : element.getBoundingClientRect().top + yOffset;

                setTimeout(() => {
                    scrollBarElement.scrollTo({ top: y, behavior: "smooth" });
                    setTimeout(() => {
                        window.location.hash = `#!${eid}`;
                    }, 1000);
                }, 2000);
            }
        }
    };

    const handleOpenIntro = ({ id }) => {
        const _opened_intros = OPENED_INTROS?.includes(id)
            ? OPENED_INTROS?.filter((f) => f !== id)
            : [...OPENED_INTROS, id];

        setOPENED_INTROS(_opened_intros);
    };

    const handleGotoDB = () => {
        dispatch(setAreaForPanel({ ...area, group_id: MODULES?.tree_group_id }));
    };

    return (
        <>
            {/* <Head>
                <title>{translationFromJson(area, "name")} | Dashboard | DBW</title>
                <meta name="description" content="DBW description" />
                <link rel="icon" href="/favicon.ico" />
            </Head> */}

            <Container maxWidth={false} id={"banner"} className={classes.rootContainer}>
                <PageTitle
                    area={translationFromJson(area, "name")}
                    modules={MODULES}
                    gotoDB={handleGotoDB}
                    isCalc={IS_CALC}
                />

                {LOADING ? (
                    <DashboardSkeleton />
                ) : (
                    <>
                        <SronlyH3>{t("Sekcja prezentująca wartości dla trzech wskaźników oraz wykresy")}</SronlyH3>
                        <Grid container spacing={downLg ? 1 : 4}>
                            <Grid
                                item
                                xs={12}
                                lg={!!MODULES?.charts?.length ? 6 : 12}
                                xl={!!MODULES?.charts?.length ? 6 : 12}
                            >
                                <Grid container spacing={downLg ? 1 : 4} sx={{ height: { md: "calc(100% + 8px)" } }}>
                                    <Grid item xs={12} md={4} lg={12}>
                                        <Grid container spacing={downXl ? (downLg ? (downMd ? 1 : 4) : 1) : 4}>
                                            {!!MODULES?.widgets?.length ? (
                                                MODULES.widgets
                                                    .slice(0, !!MODULES?.charts?.length ? 3 : MODULES?.widgets?.length)
                                                    .map((widget) => {
                                                        return (
                                                            <Grid item xs={12} sm={4} md={12} lg={4} key={widget.id}>
                                                                <HeaderInfoWidget
                                                                    title={translationFromJson(widget, "title")}
                                                                    value={translationFromJson(widget, "value")}
                                                                    description={translationFromJson(widget, "desc")}
                                                                    precision={widget.precision}
                                                                    widget={widget}
                                                                />
                                                            </Grid>
                                                        );
                                                    })
                                            ) : LOADING ? (
                                                <Preloader type={"linear"} white={"white"} />
                                            ) : (
                                                <Grid item xs={12} md></Grid>
                                            )}
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12} md={8} lg={12} sx={{ height: { md: "100%" } }}>
                                        {!!MODULES?.charts?.length ? (
                                            <Grid container spacing={4} sx={{ height: { md: "calc(100% + 32px)" } }}>
                                                {MODULES.charts
                                                    .filter((c) => c.zone === 2)
                                                    .map((topChart) => {
                                                        return (
                                                            <Grid
                                                                item
                                                                xs={12}
                                                                md={topChart.half_width ? 6 : 12}
                                                                key={topChart.id}
                                                                style={{ minHeight: 300 }}
                                                            >
                                                                <Suspense fallback={<Preloader type={"linear"} />}>
                                                                    <TopChartCard
                                                                        area={area}
                                                                        widget={topChart}
                                                                        forStory={true}
                                                                    />
                                                                </Suspense>
                                                            </Grid>
                                                        );
                                                    })}
                                            </Grid>
                                        ) : (
                                            <></>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>

                            {!!MODULES?.charts?.length ? (
                                <Grid item xs={12} lg={6} xl={6}>
                                    <Grid container spacing={4} sx={{ height: { md: "calc(100% + 32px)" } }}>
                                        {MODULES.charts
                                            .filter((c) => c.zone === 3)
                                            .map((topChart) => {
                                                return (
                                                    <Grid
                                                        item
                                                        xs={12}
                                                        md={topChart.half_width ? 6 : 12}
                                                        key={topChart.id}
                                                        sx={{ height: { md: topChart.half_height ? "50%" : "100%" } }}
                                                        style={{ minHeight: 300 }}
                                                    >
                                                        <TopChartCard area={area} widget={topChart} forStory={true} />
                                                    </Grid>
                                                );
                                            })}
                                    </Grid>
                                </Grid>
                            ) : (
                                <></>
                            )}

                            <Grid item xs={12} sx={{ pt: 5 }}>
                                <Divider
                                    sx={{
                                        borderColor: "primary.main",
                                        opacity: 0.125,
                                        borderBottomWidth: 2,
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={0}>
                            <Grid item xs={12} md={12}>
                                <Paper className={classes.ContentHolder} id={"dashboard-main-content"} tabIndex={-1}>
                                    {!!MODULES?.modules?.length ? (
                                        MODULES?.modules.map((mod, i) => {
                                            return (
                                                <Grid container spacing={0} key={`${mod.id}-${i}`} id={mod.id}>
                                                    <Grid
                                                        item
                                                        xs={12}
                                                        sx={{ pb: { xs: 2, md: 5 }, pt: { xs: 2, md: 5 } }}
                                                    >
                                                        <Typography
                                                            variant={"body1"}
                                                            color={"primary"}
                                                            className={classes.modTitle}
                                                            id={`dashboard-main-content-${i}-title`}
                                                            tabIndex={-1}
                                                            component={"h3"}
                                                        >
                                                            <b>{translationFromJson(mod, "title")}</b>
                                                        </Typography>
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                        <Grid container spacing={4}>
                                                            {!!mod?.intro && (
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    // md={4}
                                                                    xl={4}
                                                                    sx={{ paddingBottom: 0, paddingTop: 0 }}
                                                                >
                                                                    <Paper
                                                                        className={clsx(
                                                                            classes.introPaper,
                                                                            classes.boxedWrapper
                                                                        )}
                                                                        id={`intro-for-${mod.id}`}
                                                                    >
                                                                        <div
                                                                            dangerouslySetInnerHTML={{
                                                                                __html:
                                                                                    mod.intro !== "0"
                                                                                        ? `<div>${translationFromJson(
                                                                                              mod,
                                                                                              "intro"
                                                                                          )}</div>`
                                                                                        : `"0"`,
                                                                            }}
                                                                            className={clsx(
                                                                                Object.keys(INTRO_HEIGHTS)?.length
                                                                                    ? OPENED_INTROS?.includes(mod.id)
                                                                                        ? classes.introContentVisible
                                                                                        : classes.introContentHidden
                                                                                    : classes.introContentVisible
                                                                            )}
                                                                        />
                                                                        <AppTooltip
                                                                            title={
                                                                                OPENED_INTROS?.includes(mod.id)
                                                                                    ? t("Zwiń informację")
                                                                                    : t("Rozwiń informację")
                                                                            }
                                                                            arrow
                                                                            placement={"top"}
                                                                            type={"dark"}
                                                                            classes={{ tooltip: classes.tooltip }}
                                                                        >
                                                                            <span>
                                                                                <Button
                                                                                    fullWidth
                                                                                    onClick={() =>
                                                                                        handleOpenIntro({ id: mod.id })
                                                                                    }
                                                                                    sx={{ color: "#00000042" }}
                                                                                    disabled={
                                                                                        downLg
                                                                                            ? false
                                                                                            : INTRO_HEIGHTS[
                                                                                                  `${mod.id}`
                                                                                              ] +
                                                                                                  26 <=
                                                                                              MIN_INTRO_HEIGHT
                                                                                    }
                                                                                    className={clsx(
                                                                                        OPENED_INTROS?.includes(
                                                                                            mod.id
                                                                                        ) || classes.expandBtn,
                                                                                        !downLg &&
                                                                                            INTRO_HEIGHTS[`${mod.id}`] +
                                                                                                26 <=
                                                                                                MIN_INTRO_HEIGHT &&
                                                                                            classes.expandBtnDisabled
                                                                                    )}
                                                                                    aria-label={
                                                                                        OPENED_INTROS?.includes(mod.id)
                                                                                            ? t(
                                                                                                  "Zwiń informację o {{title}}",
                                                                                                  {
                                                                                                      title: translationFromJson(
                                                                                                          mod,
                                                                                                          "title"
                                                                                                      ),
                                                                                                  }
                                                                                              )
                                                                                            : t(
                                                                                                  "Rozwiń informację, aby zobaczyć jej treść"
                                                                                              )
                                                                                    }
                                                                                >
                                                                                    {OPENED_INTROS?.includes(mod.id) ? (
                                                                                        <ExpandLess color={"primary"} />
                                                                                    ) : (
                                                                                        <ExpandMore color={"primary"} />
                                                                                    )}
                                                                                </Button>
                                                                            </span>
                                                                        </AppTooltip>
                                                                    </Paper>
                                                                </Grid>
                                                            )}
                                                            {!!mod.charts?.length && (
                                                                <Grid item xs={12} 
                                                                // md={8}
                                                                xl={8}
                                                                >
                                                                    <Grid container spacing={4}>
                                                                        {mod.charts.map((chart) => {
                                                                            return (
                                                                                <Grid
                                                                                    item
                                                                                    xs={12}
                                                                                    key={chart.id}
                                                                                    id={`AC${chart.id}`}
                                                                                >
                                                                                    <Paper
                                                                                        className={clsx(
                                                                                            classes.chartPaper,
                                                                                            classes.boxedWrapper
                                                                                        )}
                                                                                    >
                                                                                        <ChartCard
                                                                                            chart={chart}
                                                                                            area={area}
                                                                                        />
                                                                                    </Paper>
                                                                                </Grid>
                                                                            );
                                                                        })}
                                                                    </Grid>
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item xs={12} sx={{ pt: 4 }}>
                                                        <Divider
                                                            sx={{
                                                                borderColor: "primary.main",
                                                                opacity: 0.125,
                                                                borderBottomWidth: 2,
                                                            }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            );
                                        })
                                    ) : LOADING ? (
                                        <Preloader type={"linear"} />
                                    ) : (
                                        <div className={classes.moduleDiv}>
                                            {/* <Grid item xs={12} sx={{ paddingBottom: 1, paddingTop: 3 }}>
                                    <Alert variant="outlined" severity="error">
                                        {t("Błąd lub brak metadanych dla modułów informacyjnych")}
                                    </Alert>
                                </Grid> */}
                                        </div>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>
                    </>
                )}
            </Container>
        </>
    );
};

export default IndexIndex;
